<template>
  <ResponsiveModal
    :variant="variant"
    :title="title"
    :description="description"
    :confirmLabel="$t('components.buttons.save')"
    :showCancel="!maxSmall"
    @confirm="$emit('update', multiple ? selection : selectedSpace)"
    @update:open="handleUpdateOpen"
  >
    <slot />
    <template #tabs>
      <TabsWithSearch
        ref="searchRef"
        v-model="tab"
        v-model:query="query"
        variant="linkedToSelector"
        tabVariant="underline"
        size="xl"
        :fullWidth="maxSmall"
        class="bg-primary"
      >
        <Tab :label="$t('components.link_to_selector.type.spaces')" />
        <Tab :label="$t('components.link_to_selector.type.assets')" />
      </TabsWithSearch>
    </template>
    <template #content>
      <LinkToSelector
        v-model="selection"
        :tab="tab"
        :query="query"
        :searching="searchRef?.searching"
        :buildingId="buildingId"
        :buildings="buildings"
        :multiple="multiple"
        class="bg-primary md:bg-secondary md:border-t"
        @clear:search="searchRef?.closeSearch()"
      />
    </template>
  </ResponsiveModal>
</template>

<script setup>
import { computed, ref } from "vue";
import { LinkToSelector } from "@/SharedComponents/LinkToSelector";
import { ResponsiveModal } from "@/SharedComponents/ResponsiveModal";
import { TabsWithSearch, Tab } from "@/SharedComponents/Tabs";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useSites } from "@/Composables/useSites";

const searchRef = ref(null);

const { maxSmall } = useBreakpoints();

const emit = defineEmits(["update:open", "update"]);

const props = defineProps({
  variant: { type: String, default: "property" },
  title: { type: String, required: false },
  description: { type: String, required: false },
  initialState: { type: Object, required: false },
  buildingId: { type: String, required: false },
  multiple: { type: Boolean, required: false },
  defaultTab: { type: [String, Number], required: false },
});

const tab = ref(props.defaultTab);
const query = ref("");

const selection = ref(
  props.initialState
    ? { ...props.initialState }
    : { buildings: [props.buildingId], children: [] }
);

const { isLoading, data } = useSites();

const buildings = computed(() => {
  if (isLoading.value) {
    return [];
  }
  return data.value;
});

const spaceId = computed(() => selection.value.children[0]);

const selectedSpace = computed(() =>
  buildings.value
    .flatMap((building) => [...building.children])
    .find((space) => space.id === spaceId.value)
);

function handleUpdateOpen(value) {
  reset();
  emit("update:open", value);
}

function reset() {
  tab.value = props.defaultTab;
  selection.value = props.initialState
    ? { ...props.initialState }
    : { buildings: [props.buildingId], children: [] };
}
</script>
